// Curated automation "pieces" for Snagcart — triggers and actions.
// `simpleLabel` / `simpleDescription` = plain language shown in the UI.

export const TRIGGER_PIECES = [
  {
    id: 'webhook',
    label: 'Webhook',
    simpleLabel: 'Outside app sends data',
    description: 'Listen for incoming HTTP POSTs. Snagcart will give you a secret URL.',
    simpleDescription: 'Use when Zapier, another website, or a custom script POSTs JSON to you.',
    icon: 'Webhook',
    tone: 'violet',
    kind: 'trigger',
    fields: [
      {
        key: 'secret',
        label: 'Secret password for the URL',
        type: 'text',
        placeholder: 'pick-any-random-string',
        help: 'Add this to the end of your webhook URL so only you can trigger it.',
      },
    ],
  },
  {
    id: 'custom_event',
    label: 'Custom event',
    simpleLabel: 'Something else happens',
    description: 'Use a webhook for any event that is not listed.',
    simpleDescription: 'For custom needs: name the event, then send JSON to the webhook URL from Zapier, Make, your app, or your website.',
    icon: 'Webhook',
    tone: 'violet',
    kind: 'trigger',
    fields: [
      {
        key: 'event_name',
        label: 'What should we call this event?',
        type: 'text',
        placeholder: 'invoice_paid, booking_cancelled, support_ticket_created',
        help: 'This name is only for humans so your team understands what starts the automation.',
      },
      {
        key: 'secret',
        label: 'Secret password for the URL',
        type: 'text',
        placeholder: 'auto-generated',
        help: 'Use this in the webhook URL so only trusted systems can trigger it.',
      },
    ],
  },
  {
    id: 'schedule',
    label: 'Schedule',
    simpleLabel: 'On a timer',
    description: 'Run on a recurring cron schedule.',
    simpleDescription: 'Run on a repeating schedule (e.g. every morning). Use Test run until scheduling is fully live.',
    icon: 'Clock',
    tone: 'sky',
    kind: 'trigger',
    fields: [
      {
        key: 'cron',
        label: 'Schedule (cron)',
        type: 'text',
        placeholder: '0 9 * * *',
        help: 'UTC time. Example: 0 9 * * * = every day at 9:00 AM UTC.',
      },
    ],
  },
  {
    id: 'crm.lead.created',
    label: 'CRM Lead created',
    simpleLabel: 'New lead in CRM',
    description: 'Fires whenever a new lead is added to your CRM.',
    simpleDescription: 'Runs when a lead is saved — from Lead Scraper, manual entry, or a form.',
    icon: 'Briefcase',
    tone: 'emerald',
    kind: 'trigger',
    fields: [],
  },
  {
    id: 'crm.lead.converted',
    label: 'CRM Lead converted',
    simpleLabel: 'Lead became a deal',
    description: 'Fires when a lead is converted into an opportunity / deal.',
    simpleDescription: 'Runs when you convert a lead into a sales opportunity.',
    icon: 'TrendingUp',
    tone: 'emerald',
    kind: 'trigger',
    fields: [],
  },
  {
    id: 'crm.deal.created',
    label: 'CRM Deal created',
    simpleLabel: 'New deal in pipeline',
    description: 'Fires when a new deal is added directly to the pipeline.',
    simpleDescription: 'Runs when a new deal is created in your CRM pipeline.',
    icon: 'CircleDollarSign',
    tone: 'amber',
    kind: 'trigger',
    fields: [],
  },
  {
    id: 'email.campaign.sent',
    label: 'Email campaign sent',
    simpleLabel: 'Marketing email finished sending',
    description: 'Fires after an email marketing campaign finishes sending.',
    simpleDescription: 'Runs after a bulk email campaign completes.',
    icon: 'Mail',
    tone: 'violet',
    kind: 'trigger',
    fields: [],
  },
  {
    id: 'form.submitted',
    label: 'Form submitted',
    simpleLabel: 'Someone submits a site form',
    description: 'Fires when a form on one of your Snagcart sites is submitted.',
    simpleDescription: 'Runs when a visitor submits a form on your website.',
    icon: 'FormInput',
    tone: 'sky',
    kind: 'trigger',
    fields: [],
  },
  {
    id: 'whatsapp.message.received',
    label: 'WhatsApp message received',
    simpleLabel: 'New WhatsApp message',
    description: 'Fires when a contact sends you a message on WhatsApp.',
    simpleDescription: 'Runs when someone messages your connected WhatsApp number.',
    icon: 'MessageCircle',
    tone: 'green',
    kind: 'trigger',
    fields: [],
  },
];

export const ACTION_PIECES = [
  {
    id: 'run_agent',
    label: 'Run AI Agent',
    simpleLabel: 'Ask AI Studio agent',
    description: 'Run an AI Studio agent and use its reply in later steps.',
    simpleDescription: 'Let an AI agent read the lead data and write a message or decision. Copy the agent ID from AI Studio.',
    icon: 'Workflow',
    tone: 'violet',
    kind: 'action',
    fields: [
      {
        key: 'agent_id',
        label: 'Agent ID (from AI Studio)',
        type: 'agent_select',
        required: true,
        placeholder: 'Choose an agent',
        help: 'Pick an AI Studio agent. The automation will send this event to that agent.',
      },
      {
        key: 'input',
        label: 'What should the agent do?',
        type: 'textarea',
        placeholder: 'Write a short welcome email for {{trigger.data.company}}. Lead notes: {{trigger.data.notes}}',
        help: 'Use smart fields like {{trigger.data.company}}. The reply is available to the next step as {{steps.0.response.output}}.',
      },
    ],
  },
  {
    id: 'http_request',
    label: 'HTTP Request',
    simpleLabel: 'Call any website API',
    description: 'Call any external API.',
    simpleDescription: 'Send data to another service (Zapier, custom API, etc.).',
    icon: 'Globe',
    tone: 'violet',
    kind: 'action',
    fields: [
      { key: 'method', label: 'Method', type: 'select', options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'], default: 'POST' },
      { key: 'url', label: 'URL', type: 'text', placeholder: 'https://example.com/webhook', required: true },
      { key: 'headers', label: 'Headers (JSON)', type: 'json', placeholder: '{"Authorization":"Bearer …"}' },
      { key: 'body', label: 'JSON body', type: 'json', placeholder: '{"email":"{{trigger.data.email}}"}' },
    ],
  },
  {
    id: 'send_email',
    label: 'Send Email',
    simpleLabel: 'Send an email',
    description: 'Send email through your configured email provider.',
    simpleDescription: 'Emails one person. Set up Mailgun/SendGrid under Marketing or Connections first.',
    icon: 'Mail',
    tone: 'violet',
    kind: 'action',
    fields: [
      { key: 'to', label: 'Send to (email address)', type: 'text', placeholder: '{{trigger.data.email}}', required: true },
      { key: 'subject', label: 'Subject line', type: 'text', placeholder: 'Welcome to Snagcart' },
      { key: 'body', label: 'Email body', type: 'textarea', placeholder: 'Hi {{trigger.data.first_name}}, …' },
    ],
  },
  {
    id: 'send_sms',
    label: 'Send SMS',
    simpleLabel: 'Send a text message',
    description: 'Send an SMS via your SMS provider.',
    simpleDescription: 'Texts one phone number. Configure SMS in Marketing settings.',
    icon: 'Phone',
    tone: 'emerald',
    kind: 'action',
    fields: [
      { key: 'to', label: 'Phone (include country code)', type: 'text', placeholder: '+14155551234', required: true },
      { key: 'body', label: 'Message', type: 'textarea', placeholder: 'Hi {{trigger.data.first_name}}, your order shipped.' },
    ],
  },
  {
    id: 'send_whatsapp',
    label: 'Send WhatsApp',
    simpleLabel: 'Send WhatsApp message',
    description: 'Send a WhatsApp message via your linked account.',
    simpleDescription: 'Requires WhatsApp connected in the platform.',
    icon: 'MessageCircle',
    tone: 'green',
    kind: 'action',
    fields: [
      { key: 'to', label: 'Phone (include country code)', type: 'text', placeholder: '+14155551234', required: true },
      { key: 'body', label: 'Message', type: 'textarea' },
    ],
  },
  {
    id: 'crm_lead_create',
    label: 'Create CRM Lead',
    simpleLabel: 'Save lead to CRM',
    description: 'Add a new lead to your Snagcart CRM.',
    simpleDescription: 'Creates a new row in your CRM with the data you map below.',
    icon: 'Briefcase',
    tone: 'emerald',
    kind: 'action',
    fields: [
      { key: 'email', label: 'Email', type: 'text', required: true, placeholder: '{{trigger.data.email}}' },
      { key: 'first_name', label: 'First name', type: 'text', placeholder: '{{trigger.data.first_name}}' },
      { key: 'last_name', label: 'Last name', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'company', label: 'Company', type: 'text', placeholder: '{{trigger.data.company}}' },
      { key: 'website', label: 'Website', type: 'text' },
      { key: 'source', label: 'Source tag', type: 'text', placeholder: 'automation' },
    ],
  },
  {
    id: 'crm_lead_update',
    label: 'Update CRM Lead',
    simpleLabel: 'Update CRM lead',
    description: 'Update status, score band, or notes on an existing CRM lead.',
    simpleDescription: 'Updates the triggering lead (or a lead ID you set) — used by triage workflows.',
    icon: 'Briefcase',
    tone: 'emerald',
    kind: 'action',
    fields: [
      { key: 'lead_id', label: 'Lead ID', type: 'text', placeholder: '{{trigger.data.id}}', help: 'Defaults to the lead that started this automation.' },
      { key: 'status', label: 'Status', type: 'text', placeholder: 'nurture' },
      { key: 'score_band', label: 'Score band', type: 'select', options: ['nurture', 'outreach', 'priority'], default: 'nurture' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Optional note to append' },
    ],
  },
  {
    id: 'bant_qualify',
    label: 'BANT Qualify',
    simpleLabel: 'Score lead with BANT',
    description: 'Score a lead with Budget, Authority, Need, Timing.',
    simpleDescription: 'Runs BANT qualification and stamps bant_status / bant_score on the lead.',
    icon: 'TrendingUp',
    tone: 'amber',
    kind: 'action',
    fields: [
      { key: 'lead_id', label: 'Lead ID', type: 'text', placeholder: '{{trigger.data.id}}' },
    ],
  },
  {
    id: 'book_meeting',
    label: 'Book Meeting',
    simpleLabel: 'Create booking link',
    description: 'Create a Cal.com booking link for the lead.',
    simpleDescription: 'Generates a booking URL (written into trigger context for later email steps).',
    icon: 'Clock',
    tone: 'sky',
    kind: 'action',
    fields: [
      { key: 'lead_id', label: 'Lead ID', type: 'text', placeholder: '{{trigger.data.id}}' },
    ],
  },
  {
    id: 'build_preview_site',
    label: 'Build Preview Site',
    simpleLabel: 'Build preview website',
    description: 'Auto-build a preview website for a lead.',
    simpleDescription: 'Creates a preview site and stores preview_url on the lead.',
    icon: 'Globe',
    tone: 'violet',
    kind: 'action',
    fields: [
      { key: 'lead_id', label: 'Lead ID', type: 'text', placeholder: '{{trigger.data.id}}' },
      { key: 'template_id', label: 'Template ID (optional)', type: 'text', placeholder: 'Leave blank for default' },
    ],
  },
  {
    id: 'generate_lead_report',
    label: 'Generate Lead Report',
    simpleLabel: 'Generate growth report',
    description: 'Build a branded PDF growth report for a lead.',
    simpleDescription: 'Creates a report and stores report_url for later outreach steps.',
    icon: 'Briefcase',
    tone: 'amber',
    kind: 'action',
    fields: [
      { key: 'lead_id', label: 'Lead ID', type: 'text', placeholder: '{{trigger.data.id}}' },
    ],
  },
  {
    id: 'crm_contact_create',
    label: 'Create CRM Contact',
    simpleLabel: 'Save contact to CRM',
    description: 'Promote a lead-like record to a full contact.',
    simpleDescription: 'Adds a full contact record in CRM.',
    icon: 'Users',
    tone: 'violet',
    kind: 'action',
    fields: [
      { key: 'email', label: 'Email', type: 'text', required: true },
      { key: 'first_name', label: 'First name', type: 'text' },
      { key: 'last_name', label: 'Last name', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'company', label: 'Company', type: 'text' },
      { key: 'job_title', label: 'Job title', type: 'text' },
    ],
  },
  {
    id: 'crm_deal_create',
    label: 'Create CRM Deal',
    simpleLabel: 'Create a deal',
    description: 'Drop an opportunity straight into the Kanban pipeline.',
    simpleDescription: 'Adds a new deal to your sales pipeline.',
    icon: 'CircleDollarSign',
    tone: 'amber',
    kind: 'action',
    fields: [
      { key: 'name', label: 'Deal name', type: 'text', required: true, placeholder: 'Inbound — {{trigger.data.company}}' },
      { key: 'amount', label: 'Amount', type: 'number', placeholder: '5000' },
      { key: 'currency', label: 'Currency', type: 'text', placeholder: 'USD' },
      { key: 'stage', label: 'Stage', type: 'select', options: ['new', 'qualified', 'proposal', 'negotiation', 'won', 'lost'], default: 'new' },
    ],
  },
  {
    id: 'slack_notify',
    label: 'Slack notification',
    simpleLabel: 'Message Slack',
    description: 'Post a message to your configured Slack workspace.',
    simpleDescription: 'Posts to Slack. Connect Slack under Connections first.',
    icon: 'Slack',
    tone: 'rose',
    kind: 'action',
    fields: [
      { key: 'text', label: 'Message', type: 'textarea', required: true, placeholder: 'New lead: {{trigger.data.email}}' },
    ],
  },
  {
    id: 'add_email_suppression',
    label: 'Add to Email suppression list',
    simpleLabel: 'Block future emails',
    description: 'Block an email from receiving future campaigns (per website).',
    simpleDescription: 'Stops marketing emails to this address for a specific website.',
    icon: 'Ban',
    tone: 'rose',
    kind: 'action',
    fields: [
      { key: 'email', label: 'Email to block', type: 'text', required: true },
      { key: 'website_id', label: 'Website ID', type: 'text', required: true, help: 'Open My Websites → open a site → copy its ID from the URL.' },
      { key: 'reason', label: 'Reason', type: 'select', options: ['manual', 'hard_bounce', 'complaint', 'unsubscribe'], default: 'manual' },
    ],
  },
  {
    id: 'delay',
    label: 'Delay',
    simpleLabel: 'Wait a few seconds',
    description: 'Pause the workflow before the next step (max 30s).',
    simpleDescription: 'Pauses up to 30 seconds before continuing.',
    icon: 'Clock',
    tone: 'zinc',
    kind: 'action',
    fields: [
      { key: 'seconds', label: 'Seconds to wait (max 30)', type: 'number', default: 5 },
    ],
  },
  {
    id: 'filter',
    label: 'Filter / Branch',
    simpleLabel: 'Only continue if…',
    description: 'Halt the workflow if a condition is NOT met.',
    simpleDescription: 'Stops the rest of the steps unless the condition matches (like “source equals lead_scraper”).',
    icon: 'Filter',
    tone: 'sky',
    kind: 'action',
    fields: [
      { key: 'left', label: 'Check this value', type: 'text', placeholder: '{{trigger.data.source}}' },
      {
        key: 'op',
        label: 'Comparison',
        type: 'select',
        options: ['equals', 'not_equals', 'contains', 'exists', 'gt', 'gte', 'lt', 'lte', 'between'],
        default: 'equals',
      },
      { key: 'right', label: 'Should match (for equals / contains / gt / lt…)', type: 'text', placeholder: 'lead_scraper' },
      { key: 'min', label: 'Min (when comparison is between)', type: 'number', placeholder: '40' },
      { key: 'max', label: 'Max (when comparison is between)', type: 'number', placeholder: '70' },
    ],
  },
];

export const ALL_PIECES = [...TRIGGER_PIECES, ...ACTION_PIECES];

export const findPiece = (id) => ALL_PIECES.find((p) => p.id === id);

/** Prefer plain-language label in UI. */
export const pieceDisplayLabel = (piece) => piece?.simpleLabel || piece?.label || 'Step';

export const pieceDisplayDescription = (piece) => piece?.simpleDescription || piece?.description || '';
